import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { useTheme } from '../contexts/ThemeContext';
import Navbar from '../components/Navbar';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import api from '../lib/api';
import { cn } from '../lib/utils';
import { 
  Headphones, Mail, MessageCircle, FileQuestion, 
  ExternalLink, CheckCircle, Send, AlertCircle
} from 'lucide-react';

const SUPPORT_TOPICS = [
  { value: 'account', label: 'Account & Login Issues' },
  { value: 'billing', label: 'Billing & Subscription' },
  { value: 'goals', label: 'Goals & Tracking' },
  { value: 'bug', label: 'Report a Bug' },
  { value: 'feature', label: 'Feature Request' },
  { value: 'other', label: 'Other' }
];

export default function Help() {
  const { user } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    topic: '',
    subject: '',
    message: ''
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.topic || !formData.subject || !formData.message) {
      toast.error('Please fill in all fields');
      return;
    }

    setSending(true);
    
    try {
      await api.post('/api/support/contact', formData);
      setSent(true);
      toast.success('Support request sent! We\'ll get back to you soon.');
    } catch (error) {
      console.error('Failed to send support request:', error);
      toast.error(error.response?.data?.detail || 'Failed to send support request. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const resetForm = () => {
    setFormData({ topic: '', subject: '', message: '' });
    setSent(false);
  };

  return (
    <div className={cn(
      "min-h-screen transition-colors duration-300",
      isDark ? "bg-[#050505]" : "bg-[#FAF9F5]"
    )}>
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 md:px-6 py-10">
        {/* Clean header */}
        <div className="mb-10">
          <p className="sl-section-tag mb-3">HELP & SUPPORT</p>
          <h1 className="text-3xl sm:text-4xl font-semibold tracking-[-0.03em] text-[#1A1A1A] leading-tight">
            How can we help?
          </h1>
          <p className="mt-3 text-base text-[#5F5F5F] max-w-xl">
            Answers, guides, and a direct line when you need one.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 mb-8">
          {/* Quick Links */}
          <Card className={cn(
            "hover:border-[#F5D5CB] transition-all cursor-pointer",
            isDark ? "glass-card card-shine" : "bg-white shadow-sm border-[#EEEAE0] hover:shadow-md"
          )} onClick={() => navigate('/faq')}>
            <CardContent className="p-6 text-center">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-3 bg-[#FDF0EC]">
                <FileQuestion className="w-6 h-6 text-coral" />
              </div>
              <h3 className={cn("font-semibold mb-1", isDark ? "text-white" : "text-[#1A1A1A]")}>FAQ</h3>
              <p className={cn("text-sm", isDark ? "text-zinc-500" : "text-[#9E8778]")}>Find answers to common questions</p>
            </CardContent>
          </Card>

          <Card className={cn(
            "hover:border-[#F5D5CB] transition-all",
            isDark ? "glass-card card-shine" : "bg-white shadow-sm border-[#EEEAE0] hover:shadow-md"
          )}>
            <CardContent className="p-6 text-center">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-3 bg-[#FDF0EC]">
                <Mail className="w-6 h-6 text-coral" />
              </div>
              <h3 className={cn("font-semibold mb-1", isDark ? "text-white" : "text-[#1A1A1A]")}>Email Us</h3>
              <a href="mailto:support@monkmode.fit" className="text-sm text-coral hover:underline flex items-center justify-center gap-1">
                support@monkmode.fit
                <ExternalLink className="w-3 h-3" />
              </a>
            </CardContent>
          </Card>

          <Card className={cn(
            "hover:border-[#F5D5CB] transition-all",
            isDark ? "glass-card card-shine" : "bg-white shadow-sm border-[#EEEAE0] hover:shadow-md"
          )}>
            <CardContent className="p-6 text-center">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-3 bg-[#FDF0EC]">
                <MessageCircle className="w-6 h-6 text-coral" />
              </div>
              <h3 className={cn("font-semibold mb-1", isDark ? "text-white" : "text-[#1A1A1A]")}>Response Time</h3>
              <p className={cn("text-sm", isDark ? "text-zinc-500" : "text-[#9E8778]")}>Usually within 24 hours</p>
            </CardContent>
          </Card>
        </div>

        {/* Contact Form */}
        <Card className={cn(isDark ? "glass-card card-shine" : "bg-white shadow-sm border-[#EEEAE0]")}>
          <CardHeader>
            <CardTitle className={cn("flex items-center gap-2", isDark ? "text-gradient" : "text-[#1A1A1A]")}>
              <Send className="w-5 h-5" />
              Contact Support
            </CardTitle>
            <CardDescription className={isDark ? "text-zinc-400" : "text-[#9E8778]"}>
              Describe your issue and we&apos;ll get back to you as soon as possible
            </CardDescription>
          </CardHeader>
          <CardContent>
            {sent ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 bg-[#FDF0EC]">
                  <CheckCircle className="w-8 h-8 text-coral" />
                </div>
                <h3 className={cn("text-xl font-semibold mb-2", isDark ? "text-white" : "text-[#1A1A1A]")}>Request Sent!</h3>
                <p className={cn("mb-6", isDark ? "text-zinc-400" : "text-[#9E8778]")}>
                  We&apos;ve received your support request. Our team will review it and get back to you at <strong className={isDark ? "text-white" : "text-[#1A1A1A]"}>{user?.email}</strong> within 24 hours.
                </p>
                <Button onClick={resetForm} variant="outline" className={cn(isDark ? "border-zinc-700 text-zinc-300 hover:bg-zinc-800" : "border-[#E5E2DA] text-[#5F5F5F] hover:bg-white")}>
                  Send Another Request
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="topic" className={isDark ? "text-zinc-300" : "text-[#1A1A1A]"}>Topic *</Label>
                    <Select 
                      value={formData.topic} 
                      onValueChange={(v) => setFormData({...formData, topic: v})}
                    >
                      <SelectTrigger className={isDark ? "bg-zinc-800 border-zinc-700 text-zinc-300" : "bg-white border-[#E5E2DA] text-[#1A1A1A]"}>
                        <SelectValue placeholder="Select a topic" />
                      </SelectTrigger>
                      <SelectContent className={isDark ? "bg-zinc-900 border-zinc-800" : "bg-white border-[#E5E2DA]"}>
                        {SUPPORT_TOPICS.map(topic => (
                          <SelectItem key={topic.value} value={topic.value} className={isDark ? "text-zinc-300 focus:text-white focus:bg-zinc-800" : "text-[#1A1A1A] focus:text-slate-900 focus:bg-[#F4F3EE]"}>
                            {topic.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className={isDark ? "text-zinc-300" : "text-[#1A1A1A]"}>Your Email</Label>
                    <Input 
                      value={user?.email || ''} 
                      disabled 
                      className={isDark ? "bg-zinc-800/50 border-zinc-700 text-zinc-400" : "bg-slate-50 border-[#E5E2DA] text-[#5F5F5F]"}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="subject" className={isDark ? "text-zinc-300" : "text-[#1A1A1A]"}>Subject *</Label>
                  <Input
                    id="subject"
                    placeholder="Brief description of your issue"
                    value={formData.subject}
                    onChange={(e) => setFormData({...formData, subject: e.target.value})}
                    className={isDark ? "bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500" : "bg-white border-[#E5E2DA] text-[#1A1A1A] placeholder:text-[#9E8778]"}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message" className={isDark ? "text-zinc-300" : "text-[#1A1A1A]"}>Message *</Label>
                  <Textarea
                    id="message"
                    placeholder="Please provide as much detail as possible..."
                    rows={5}
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    className={isDark ? "bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500" : "bg-white border-[#E5E2DA] text-[#1A1A1A] placeholder:text-[#9E8778]"}
                  />
                </div>

                <div className={cn(
                  "flex items-start gap-2 p-3 rounded-lg border",
                  "bg-[#FDF0EC] border-[#F5D5CB]"
                )}>
                  <AlertCircle className="w-5 h-5 text-coral flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-[#5F5F5F]">
                    For account security issues or urgent matters, please include your account email and any relevant transaction IDs.
                  </p>
                </div>

                <div className="flex justify-end">
                  <Button type="submit" disabled={sending} className="gap-2 btn-glossy">
                    {sending ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4" />
                        Send Request
                      </>
                    )}
                  </Button>
                </div>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
